import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import ProfileBar from "../../Organisms/App Utility Bar/profilebar.js";

import { useDeals } from "../../CustomHooks/Queries";

import "./cash.scss";

const MM_CARDS = [
  {
    key: "add-cash",
    title: "Add cash",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M12 4V20" stroke="#54FF99" strokeWidth="2" strokeLinecap="round" />
        <path d="M4 12H20" stroke="#54FF99" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
    route: "/add-cash",
  },
  {
    key: "cash-out",
    title: "Cash out",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M12 20V4" stroke="#54FF99" strokeWidth="2" strokeLinecap="round" />
        <path d="M7 9L12 4L17 9" stroke="#54FF99" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    route: "/cash-out",
  },
  // Hidden for v1 — no backend integration yet
  // {
  //   key: "deposit",
  //   title: "Deposit check",
  //   route: null,
  // },
  // {
  //   key: "cash",
  //   title: "Deposit cash",
  //   route: null,
  // },
];

const MM_ROWS = [
  {
    key: "direct-deposit",
    label: "Set up direct deposit",
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M10 2L2 6L10 10L18 6L10 2Z" stroke="#54FF99" strokeWidth="1.4" strokeLinejoin="round" />
        <path d="M2 10L10 14L18 10" stroke="#54FF99" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M2 14L10 18L18 14" stroke="#54FF99" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    route: "/direct-deposit",
  },
  {
    key: "venmo",
    label: "Cash out with Venmo",
    icon: (
      <svg width="20" height="20" viewBox="0 0 640 640" fill="none">
        <path
          d="M530.5 78.8C547.9 107.5 555.8 137 555.8 174.3C555.8 293.3 453.9 447.8 371.1 556.4L182.2 556.4L106.4 104.9L271.8 89.2L311.9 410.5C349.3 349.7 395.5 254.2 395.5 189.1C395.5 153.5 389.4 129.2 379.8 109.2L530.5 78.8z"
          fill="#54FF99"
        />
      </svg>
    ),
    route: "/venmo-transfer",
  },
  {
    key: "mail-a-check",
    label: "Mail a check",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <rect x="2.5" y="6" width="19" height="12" rx="2" stroke="#54FF99" strokeWidth="1.5" strokeLinejoin="round" />
        <path d="M2.5 8L12 13.5L21.5 8" stroke="#54FF99" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    route: "/mail-a-check",
  },
  {
    key: "recurring",
    label: "Manage recurring transfers",
    icon: (
      <svg width="20" height="20" viewBox="0 0 640 640" fill="none">
        <path
          d="M537 177C546.4 167.6 546.4 152.4 537 143.1L449 55C439.6 45.6 424.4 45.6 415.1 55C405.8 64.4 405.7 79.6 415.1 88.9L462.1 135.9L256 135.9C150 136 64 222 64 328C64 341.3 74.7 352 88 352C101.3 352 112 341.3 112 328C112 248.5 176.5 184 256 184L462.1 184L415.1 231C405.7 240.4 405.7 255.6 415.1 264.9C424.5 274.2 439.7 274.3 449 264.9L537 176.9zM103 463C98.5 467.5 96 473.6 96 480C96 486.4 98.5 492.5 103 497L191 585C200.4 594.4 215.6 594.4 224.9 585C234.2 575.6 234.3 560.4 224.9 551.1L177.9 504.1L384 504.1C490 504.1 576 418.1 576 312.1C576 298.8 565.3 288.1 552 288.1C538.7 288.1 528 298.8 528 312.1C528 391.6 463.5 456.1 384 456.1L177.9 456.1L224.9 409.1C234.3 399.7 234.3 384.5 224.9 375.2C215.5 365.9 200.3 365.8 191 375.2L103 463z"
          fill="#54FF99"
        />
      </svg>
    ),
    route: "/recurring-deposits",
  },
];

function ChevronRight() {
  return (
    <svg className="mm-row-chevron" width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M6 4l4 4-4 4" stroke="#8B8FA3" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function CashPage() {
  const navigate = useNavigate();
  const { data: deals } = useDeals();

  const cashout = deals?.data?.data?.currentDeal?.cashout ?? {};

  const filteredCards = useMemo(() => {
    return MM_CARDS.filter((c) => {
      if (c.key === "cash-out") {
        return !!cashout.is_external_bank_allowed;
      }
      return true;
    });
  }, [cashout.is_external_bank_allowed]);

  const filteredRows = useMemo(() => {
    return MM_ROWS.filter((r) => {
      if (r.key === "venmo") {
        return !!cashout.is_venmo_allowed;
      }
      if (r.key === "mail-a-check") {
        return !!cashout.is_check_allowed;
      }
      return true;
    });
  }, [cashout.is_venmo_allowed, cashout.is_check_allowed]);

  const [animReady, setAnimReady] = useState(false);
  const hasTriggeredAnim = useRef(false);

  useEffect(() => {
    if (!hasTriggeredAnim.current) {
      hasTriggeredAnim.current = true;
      requestAnimationFrame(() => setAnimReady(true));
    }
  }, []);

  return (
    <div className={`cash-page${animReady ? " anim-ready" : ""}`}>
      <ProfileBar />

      <div className="mm-cards">
        <div className="mm-cards-row">
          {filteredCards.slice(0, 2).map((c) => (
            <div
              key={c.key}
              className="mm-card"
              onClick={c.route ? () => navigate(c.route) : undefined}
            >
              <div className="mm-card-icon">{c.icon}</div>
              <span className="mm-card-title">{c.title}</span>
            </div>
          ))}
        </div>
        {/* Hidden for v1 — second row commented out, no cards to render */}
        {/* <div className="mm-cards-row">
          {MM_CARDS.slice(2).map((c) => (
            <div
              key={c.key}
              className="mm-card"
              onClick={c.route ? () => navigate(c.route) : undefined}
            >
              <div className="mm-card-icon">{c.icon}</div>
              <span className="mm-card-title">{c.title}</span>
            </div>
          ))}
        </div> */}
      </div>

      <div className="mm-list">
        {filteredRows.map((r) => (
          <div key={r.key} className="mm-row" onClick={() => navigate(r.route)}>
            <div className="mm-row-icon">{r.icon}</div>
            <span className="mm-row-label">{r.label}</span>
            <ChevronRight />
          </div>
        ))}
      </div>
    </div>
  );
}
